import React, { useState, useEffect } from "react"
import {
    Dialog,
    DialogContent,
    DialogTitle,
    FormGroup,
    FormControlLabel,
    Switch,
    Button,
    Typography,
    makeStyles,
} from "@material-ui/core"
import { withStyles } from "@material-ui/core/styles"
import CookieIcon from "../../assets/Cookies/cookie.png"
import { loadTrackingScripts } from "../../utils/loadTrackingScripts"

const useButtonStyles = makeStyles((theme) => ({
    buttonsContainer: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: "2rem",
        marginBottom: "2rem",
        gap: "0.75rem",

        [theme.breakpoints.down("xs")]: {
            flexDirection: "column",
            alignItems: "stretch",
        },
    },
    primaryButton: {
        backgroundColor: "#002B3E",
        color: "#fff",
        borderRadius: "6px",
        textTransform: "none",
        padding: "10px 16px",
        fontSize: "0.85rem",
        width: "100%",

        "&:hover": {
            backgroundColor: "#001F2C",
        },

        [theme.breakpoints.up("sm")]: {
            width: "auto",
            fontSize: "0.8rem",
        },
    },
    outlinedButton: {
        borderColor: "#002B3E",
        color: "#002B3E",
        borderRadius: "6px",
        textTransform: "none",
        padding: "10px 16px",
        fontSize: "0.85rem",
        width: "100%",

        "&:hover": {
            borderColor: "#001F2C",
            color: "#001F2C",
            backgroundColor: "rgba(0,43,62,0.05)",
        },

        [theme.breakpoints.up("sm")]: {
            width: "auto",
            fontSize: "0.8rem",
        },
    },
}));

const useTitleStyles = makeStyles((theme) => ({
    title: {
        fontSize: "1.3rem",
        color: "#002B3E",
        lineHeight: 1.2,
        fontWeight: 600,

        [theme.breakpoints.down("xs")]: {
            fontSize: "1.1rem",
        },
    },
    icon: {
        width: 52,
        height: 52,

        [theme.breakpoints.down("xs")]: {
            width: 40,
            height: 40,
        },
    },
}));

const styles = (theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },
});

const CustomDialogTitle = withStyles(styles)(({ children, classes, ...other }) => (
    <DialogTitle disableTypography className={classes.root} {...other}>
        {children}
    </DialogTitle>
));

const CookieModal = ({ onClose }) => {
    const [open, setOpen] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const [prefs, setPrefs] = useState({
        necessary: true,
        analytics: false,
        marketing: false,
    });
    const buttonClasses = useButtonStyles();
    const titleClasses = useTitleStyles();
    useEffect(() => {
        const open = () => setOpen(true)
        window.addEventListener("open-cookie-modal", open)
        return () => window.removeEventListener("open-cookie-modal", open)
    }, [])

    useEffect(() => {
        const savedConsent = localStorage.getItem("cookieConsent");
        if (savedConsent) {
            const parsed = JSON.parse(savedConsent);
            loadTrackingScripts(parsed);
            onClose();
        } else {
            setOpen(true);
        }
    }, []);

    const handleClose = () => {
        setOpen(false);
        onClose();
    };

    const handleToggle = (name) => {
        setPrefs((prev) => ({
            ...prev,
            [name]: !prev[name],
        }));
    };

    const handleAcceptAll = () => {
        const allPrefs = { necessary: true, analytics: true, marketing: true };
        localStorage.setItem("cookieConsent", JSON.stringify(allPrefs));
        loadTrackingScripts(allPrefs);
        setOpen(false);
    };

    const handleRejectAll = () => {
        const rejected = { necessary: true, analytics: false, marketing: false };
        localStorage.setItem("cookieConsent", JSON.stringify(rejected));
        setOpen(false);
    };

    const handleSave = () => {
        localStorage.setItem("cookieConsent", JSON.stringify(prefs));
        loadTrackingScripts(prefs);
        setOpen(false);
    };

    const handlePreferences = () => {
        setShowPreferences(true);
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            onClose={(event, reason) => {
                if (reason === "backdropClick") return;
                handleClose();
            }}
            aria-labelledby="cookie-dialog-title"
            scroll="paper"
        >
            <CustomDialogTitle id="cookie-dialog-title">
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <img
                        src={CookieIcon}
                        alt="Cookie icon"
                        className={titleClasses.icon}
                    />
                    <Typography className={titleClasses.title} component="h2">
                        Utilizamos cookies
                    </Typography>
                </div>
            </CustomDialogTitle>

            <DialogContent>
                <Typography variant="body1" gutterBottom>
                    Usamos cookies para mejorar su experiencia de navegación en nuestra web, para
                    mostrarle contenidos personalizados y para analizar el tráfico en nuestra web.
                </Typography>

                <div className={buttonClasses.buttonsContainer}>
                    <Button
                        className={buttonClasses.primaryButton}
                        variant="contained"
                        onClick={() => {
                            handleAcceptAll();
                            handleClose();
                        }}
                    >
                        Aceptar todas
                    </Button>

                    <Button
                        className={buttonClasses.outlinedButton}
                        variant="outlined"
                        onClick={() => {
                            handleRejectAll();
                            handleClose();
                        }}
                    >
                        Rechazar no esenciales
                    </Button>

                    <Button
                        className={buttonClasses.outlinedButton}
                        variant="outlined"
                        onClick={handlePreferences}
                    >
                        Configurar preferencias
                    </Button>
                </div>

                {/* PREFERENCIAS */}
                {showPreferences && (
                    <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
                        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                            Preferencias
                        </Typography>

                        <FormGroup>
                            <FormControlLabel
                                control={<Switch checked disabled />}
                                label="Técnicas / Esenciales"
                            />
                            <Typography variant="body2" color="textSecondary">
                                Esta opción no se puede desactivar, es necesaria para el funcionamiento del sitio.
                            </Typography>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={prefs.analytics}
                                        onChange={() => handleToggle("analytics")}
                                    />
                                }
                                label="Analíticas"
                            />
                            <Typography variant="body2" color="textSecondary">
                                Únicamente tiene uso interno para medir acciones.
                            </Typography>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={prefs.marketing}
                                        onChange={() => handleToggle("marketing")}
                                    />
                                }
                                label="Marketing / Publicitarias"
                            />
                            <Typography variant="body2" color="textSecondary">
                                Se utilizan para mostrarte contenido y anuncios relevantes según tus intereses.
                            </Typography>

                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
                                <Button
                                    className={buttonClasses.primaryButton}
                                    variant="contained"
                                    onClick={() => {
                                        handleSave();
                                        handleClose();
                                    }}
                                >
                                    Guardar preferencias
                                </Button>
                            </div>
                        </FormGroup>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export { CookieModal };
